import { useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { toast } from 'sonner'
import {
  Loader2, ExternalLink, CheckCircle2, Clock, XCircle,
  UserPlus, MessageSquare, Filter, FileText, ChevronDown, Copy,
} from 'lucide-react'

function cleanGroupName(raw: string): string {
  return raw.replace(/[ÚU]ltima atividade.*/i, '').trim()
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface GroupPost {
  id: string
  agent_page_group_id: string
  group_name: string | null
  group_url: string | null
  connection_id: string | null
  post_url: string | null
  post_type: string | null
  comment_text: string | null
  status: string
  invites_sent: number
  error_message: string | null
  posted_at: number | null
}

type StatusFilter = 'all' | 'posted' | 'commented' | 'invited' | 'failed'

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: typeof CheckCircle2 }> = {
  posted: { label: 'Postado', color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: CheckCircle2 },
  commented: { label: 'Comentado', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: MessageSquare },
  invited: { label: 'Convidado', color: 'text-indigo-400', bg: 'bg-indigo-500/10', icon: UserPlus },
  failed: { label: 'Falhou', color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function PostsTab() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: postsRaw, isLoading } = useQuery<{ posts: GroupPost[] }>({
    queryKey: ['automacao-fb', 'all-group-posts'],
    queryFn: () => api.get('/automacao-fb/group-posts'),
    refetchInterval: 30000,
  })

  const posts: GroupPost[] = postsRaw?.posts ?? []

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return posts
    return posts.filter(p => p.status === statusFilter)
  }, [posts, statusFilter])

  // Stats
  const stats = useMemo(() => {
    const byStatus: Record<string, number> = {}
    for (const p of posts) {
      byStatus[p.status] = (byStatus[p.status] || 0) + 1
    }
    return {
      total: posts.length,
      posted: byStatus['posted'] || 0,
      commented: byStatus['commented'] || 0,
      invited: byStatus['invited'] || 0,
      failed: byStatus['failed'] || 0,
      totalInvites: posts.reduce((sum, p) => sum + (p.invites_sent || 0), 0),
    }
  }, [posts])

  return (
    <div className="space-y-4">
      {/* ── Stats bar ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <StatBadge
          label="Total"
          value={stats.total}
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
          className="border-[#27272a] bg-[#111113]"
        />
        <StatBadge
          label="Postados"
          value={stats.posted}
          active={statusFilter === 'posted'}
          onClick={() => setStatusFilter(statusFilter === 'posted' ? 'all' : 'posted')}
          className="border-emerald-500/15 bg-emerald-500/5"
          textColor="text-emerald-300"
        />
        <StatBadge
          label="Comentados"
          value={stats.commented}
          active={statusFilter === 'commented'}
          onClick={() => setStatusFilter(statusFilter === 'commented' ? 'all' : 'commented')}
          className="border-blue-500/15 bg-blue-500/5"
          textColor="text-blue-300"
        />
        <StatBadge
          label="Convidados"
          value={stats.invited}
          active={statusFilter === 'invited'}
          onClick={() => setStatusFilter(statusFilter === 'invited' ? 'all' : 'invited')}
          className="border-indigo-500/15 bg-indigo-500/5"
          textColor="text-indigo-300"
        />
        <StatBadge
          label="Falharam"
          value={stats.failed}
          active={statusFilter === 'failed'}
          onClick={() => setStatusFilter(statusFilter === 'failed' ? 'all' : 'failed')}
          className="border-red-500/15 bg-red-500/5"
          textColor="text-red-300"
        />

        <div className="ml-auto flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-500/5 border border-indigo-500/10">
          <UserPlus className="h-3.5 w-3.5 text-indigo-400" />
          <span className="text-xs text-indigo-300">{stats.totalInvites} convites total</span>
        </div>
      </div>

      {/* ── Posts table ── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-600">
            {posts.length === 0 ? 'Nenhum post registrado' : 'Nenhum post com esse filtro'}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-[#27272a] overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_120px_100px_80px_120px_40px] gap-3 px-4 py-2.5 bg-[#0c0c10] border-b border-[#27272a]">
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Grupo</span>
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Status</span>
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Convites</span>
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Tipo</span>
            <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold">Data</span>
            <span />
          </div>

          {/* Table body */}
          <div className="divide-y divide-[#1e1e22]">
            {filtered.map(post => {
              const cfg = STATUS_CONFIG[post.status] || STATUS_CONFIG.failed
              const Icon = cfg.icon
              const isExpanded = expandedId === post.id
              const hasError = !!post.error_message

              return (
                <div key={post.id}>
                  <div
                    onClick={() => hasError && setExpandedId(isExpanded ? null : post.id)}
                    className={`grid grid-cols-[1fr_120px_100px_80px_120px_40px] gap-3 px-4 py-3 items-center hover:bg-[#111113] transition-colors ${hasError ? 'cursor-pointer' : ''}`}
                  >
                    {/* Group */}
                    <div className="min-w-0 flex items-center gap-1.5">
                      {hasError && (
                        <ChevronDown className={`h-3 w-3 text-zinc-600 shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                      )}
                      <div className="min-w-0">
                        <p className="text-xs text-zinc-300 truncate">{post.group_name ? cleanGroupName(post.group_name) : '—'}</p>
                        {hasError && !isExpanded && (
                          <p className="text-[10px] text-red-400/70 truncate mt-0.5">{post.error_message}</p>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded ${cfg.bg} ${cfg.color} w-fit`}>
                      <Icon className="h-3 w-3" />
                      {cfg.label}
                    </span>

                    {/* Invites */}
                    <div>
                      {post.invites_sent > 0 ? (
                        <span className="text-xs text-indigo-300 font-medium">{post.invites_sent}</span>
                      ) : (
                        <span className="text-xs text-zinc-700">—</span>
                      )}
                    </div>

                    {/* Type */}
                    <span className="text-[11px] text-zinc-500">{post.post_type || '—'}</span>

                    {/* Date */}
                    <span className="text-[11px] text-zinc-600">
                      {post.posted_at
                        ? new Date(post.posted_at).toLocaleString('pt-BR', {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                          })
                        : '—'
                      }
                    </span>

                    {/* Link */}
                    {post.post_url ? (
                      <a
                        href={post.post_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-600 hover:text-indigo-400 transition-colors"
                        title="Abrir post"
                        onClick={e => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span />
                    )}
                  </div>

                  {/* Expanded error detail */}
                  {isExpanded && hasError && (
                    <div className="px-4 pb-3 pt-0">
                      <div className="rounded-lg border border-red-500/20 bg-red-950/20 p-3 flex items-start gap-2">
                        <pre className="flex-1 text-[11px] text-red-300/80 whitespace-pre-wrap break-all font-mono leading-relaxed">
                          {post.error_message}
                        </pre>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(post.error_message ?? '')
                            toast.success('Erro copiado')
                          }}
                          className="shrink-0 rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 transition-colors"
                          title="Copiar erro"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function StatBadge({
  label,
  value,
  active,
  onClick,
  className = '',
  textColor = 'text-zinc-300',
}: {
  label: string
  value: number
  active: boolean
  onClick: () => void
  className?: string
  textColor?: string
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${className} ${
        active ? 'ring-1 ring-indigo-500/40' : 'opacity-70 hover:opacity-100'
      }`}
    >
      <span className={`text-sm font-bold font-mono ${textColor}`}>{value}</span>
      <span className="text-[10px] text-zinc-500">{label}</span>
    </button>
  )
}
